import { useParams, Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useCompetitorPrices, formatChecked, fmtNum } from "@/lib/competitorPrices";
import { ArrowRight, Check, X, ArrowLeft, Shield, Zap, DollarSign, Globe } from "lucide-react";

const vsData = {
  "slimsteprijs-nl": {
    name: "SlimstePrijs.nl", country: "Nederland",
    intro: "SlimstePrijs.nl is een Nederlandse concurrent gericht op dynamische pricing voor webshops. Ze bieden vergelijkbare basisfunctionaliteit maar tegen aanzienlijk hogere prijzen, vooral voor middelgrote en grote webshops.",
    prices: [
      { plan: "1.000 producten", sp: 75, other: 79 },
      { plan: "7.500 producten", sp: "99*", other: 199 },
      { plan: "25.000 producten", sp: 245, other: 399 },
      { plan: "50.000 producten", sp: 729, other: 759 },
    ],
    advantages: [
      "Tot 75% goedkoper bij middelgrote en grote pakketten",
      "Onbeperkt WooCommerce shops koppelen (SlimstePrijs: 1 shop)",
      "B2B facturatie met automatische BTW berekening per EU-land",
      "Intelligente AI-matching op basis van EAN + semantische analyse",
      "Meertalig platform (NL/EN/FR) - SlimstePrijs is alleen NL",
      "Affiliate programma om extra inkomsten te genereren",
      "Belgisch bedrijf = EU wetgeving, GDPR compliant",
      "Repricing voorstellen met accept/weiger (niet blind automatisch)",
    ],
    disadvantages: [
      "SlimstePrijs biedt 30 dagen gratis trial (Smartpriced: 14 dagen)",
      "SlimstePrijs heeft Shopify integratie (Smartpriced: binnenkort)",
    ],
  },
  "prisync": {
    name: "Prisync", country: "Turkije",
    intro: "Prisync is een internationale prijsmonitoring tool uit Istanbul, Turkije. Populair bij internationale e-commerce bedrijven, maar met beperkte Benelux focus en hogere prijzen. Prijzen in USD.",
    prices: [
      { plan: "100 producten", sp: 45, other: 99 },
      { plan: "1.000 producten", sp: 75, other: 199 },
      { plan: "5.000 producten", sp: 99, other: 399 },
    ],
    advantages: [
      "Tot 50% goedkoper voor vergelijkbare functionaliteit",
      "B2B facturatie met EU BTW regels (Prisync biedt dit niet)",
      "Onbeperkt shops per abonnement",
      "Belgisch bedrijf met EU dataprivacy garantie",
      "Meertalig platform voor de Benelux markt",
      "Geen data opslag buiten de EU",
    ],
    disadvantages: [
      "Prisync heeft een grotere internationale community",
      "Prisync ondersteunt meer e-commerce platformen",
    ],
  },
  "competera": {
    name: "Competera", country: "VS/Oekraine",
    intro: "Competera is een enterprise-level pricing optimization platform. Gericht op grote retailers met complexe assortimenten. De hoge drempel en ondoorzichtige prijzen maken het ontoegankelijk voor MKB.",
    prices: [],
    advantages: [
      "Transparante, vaste prijzen (Competera: alleen op aanvraag)",
      "Geen sales gesprek nodig om te starten",
      "Toegankelijk voor MKB webshops en groothandels",
      "Zelfde kernfuncties: monitoring, matching, repricing",
      "Direct operationeel in 2 minuten",
    ],
    disadvantages: [
      "Competera biedt meer geavanceerde AI/ML pricing modellen",
      "Competera heeft grotere enterprise klanten als referentie",
    ],
  },
  "price2spy": {
    name: "Price2Spy", country: "Servie",
    intro: "Price2Spy is een Servische prijsmonitoring tool die al sinds 2011 bestaat. Ervaren speler maar met verouderde interface en beperkte Benelux focus. Prijzen in USD.",
    prices: [
      { plan: "500 URLs", sp: 45, other: 39.95 },
      { plan: "2.000 URLs", sp: 75, other: 157.95 },
    ],
    advantages: [
      "Tot 60% goedkoper voor vergelijkbare pakketten",
      "Moderne interface vs. verouderd Price2Spy dashboard",
      "WooCommerce integratie standaard (Price2Spy: extra setup)",
      "B2B facturatie met EU BTW regels",
      "Belgisch bedrijf, data blijft in EU",
    ],
    disadvantages: [
      "Price2Spy bestaat langer en heeft meer ervaring",
      "Price2Spy ondersteunt meer datapunten per product",
    ],
  },
  "omnia-retail": {
    name: "Omnia Retail", country: "Nederland",
    intro: "Omnia Retail is een premium dynamic pricing platform voor grote retailers. Met prijzen vanaf €2.000+/maand is het volledig buiten bereik voor MKB webshops.",
    prices: [],
    advantages: [
      "MKB-vriendelijke prijzen (Omnia: €2.000+/maand)",
      "Geen langlopende contracten (Omnia: jaarcontracten)",
      "Direct starten zonder implementatietraject",
      "Dezelfde kerndata: concurrentieprijzen en margeanalyse",
    ],
    disadvantages: [
      "Omnia biedt meer geavanceerde pricing strategieen",
      "Omnia heeft directe integraties met grote marktplaatsen",
    ],
  },
  "paarly": {
    name: "Paarly", country: "Frankrijk",
    intro: "Paarly is een Franse prijsmonitoring tool met focus op de mode- en retailsector. Beperkte aanwezigheid in de Benelux.",
    prices: [],
    advantages: [
      "Werkt voor alle productcategorieen (Paarly: focus op mode)",
      "Benelux gericht met NL/EN/FR ondersteuning",
      "Transparante prijzen online beschikbaar",
      "B2B facturatie met correcte EU BTW regels",
    ],
    disadvantages: [
      "Paarly heeft meer ervaring in de modesector",
      "Paarly biedt visuele product matching",
    ],
  },
};

export default function VsPage() {
  const { slug } = useParams();
  const { t } = useI18n();
  const live = useCompetitorPrices()?.[slug];
  const base = vsData[slug];
  // Prices come from the monthly crawl; hard-coded values are only a fallback
  const vs = base && live ? {
    ...base,
    prices: live.plans.filter((p) => p.sp != null).map((p) => ({ plan: p.label && /URLs/.test(p.label) ? p.label : `${fmtNum(p.products)} producten`, sp: p.sp, other: p.other })),
  } : base;

  if (!vs) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#1A3F6E] mb-4">Vergelijking niet gevonden</h1>
          <Link to="/vergelijk"><Button className="bg-[#2CA8AC]">Terug naar overzicht</Button></Link>
        </div>
      </div>
    );
  }

  const maxSaving = vs.prices.length > 0 ? Math.max(...vs.prices.map(p => p.other ? Math.round((1 - parseFloat(p.sp) / p.other) * 100) : 0)) : 0;

  return (
    <div className="min-h-screen bg-white" data-testid="vs-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100/60">
        <div className="max-w-5xl mx-auto px-6 h-[72px] flex items-center justify-between">
          <Link to="/"><img src="/logo.jpg" alt="Smartpriced" className="h-10" /></Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to="/register"><Button className="bg-[#2CA8AC] hover:bg-[#248F92] rounded-full px-6">Start Gratis</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Link to="/vergelijk" className="inline-flex items-center gap-2 text-sm text-[#2CA8AC] hover:text-[#248F92] mb-6">
            <ArrowLeft className="w-4 h-4" /> {t("vs.back")}
          </Link>
          <h1 className="text-[2.5rem] sm:text-[3rem] font-bold text-[#1A3F6E] leading-tight">
            Smartpriced vs {vs.name}
          </h1>
          <p className="mt-4 text-lg text-slate-500">{vs.intro}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Badge className="bg-[#85D8DB]/10 text-[#1A3F6E] border-[#2CA8AC]/20 py-1.5 px-4"><Globe className="w-3.5 h-3.5 mr-1.5" />{vs.country}</Badge>
            {maxSaving > 0 && <Badge className="bg-[#2CA8AC]/10 text-[#2CA8AC] border-[#2CA8AC]/20 py-1.5 px-4"><DollarSign className="w-3.5 h-3.5 mr-1.5" />Tot {maxSaving}% goedkoper</Badge>}
          </div>
        </div>
      </section>

      {/* Price comparison */}
      {vs.prices.length > 0 && (
        <section className="py-16 px-6 bg-slate-50/50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-[#1A3F6E] mb-8">Prijsvergelijking</h2>
            <div className="space-y-4">
              {vs.prices.map((p, i) => {
                const saving = p.other ? Math.round((1 - parseFloat(p.sp) / p.other) * 100) : 0;
                return (
                  <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#1A3F6E]">{p.plan}</p>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-xs text-slate-400 mb-1">Smartpriced</p>
                        <p className="text-2xl font-bold text-[#2CA8AC] font-mono-data">€{p.sp}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-400 mb-1">{vs.name}</p>
                        <p className="text-2xl font-bold text-slate-400 font-mono-data line-through">€{p.other}</p>
                      </div>
                      {saving > 0 && (
                        <Badge className="bg-[#2CA8AC]/10 text-[#2CA8AC] border-[#2CA8AC]/20 text-sm py-1 px-3">{saving}% goedkoper</Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-4">Laatst gecontroleerd: {formatChecked(live?.last_checked)}. {vs.name.includes("Prisync") || vs.name.includes("Price2Spy") ? "Prijzen concurrent in USD. " : ""}Controleer de website van de aanbieder voor actuele tarieven.</p>
          </div>
        </section>
      )}

      {/* Advantages / Disadvantages */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
          <div>
            <h2 className="text-xl font-bold text-[#1A3F6E] mb-6 flex items-center gap-2"><Zap className="w-5 h-5 text-[#2CA8AC]" />{t("vs.advantages")}</h2>
            <div className="space-y-3">
              {vs.advantages.map((a, i) => (
                <div key={i} className="flex items-start gap-3 bg-[#85D8DB]/5 rounded-xl p-4">
                  <Check className="w-5 h-5 text-[#2CA8AC] shrink-0 mt-0.5" />
                  <p className="text-sm text-[#1A3F6E]">{a}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#1A3F6E] mb-6 flex items-center gap-2"><Shield className="w-5 h-5 text-slate-400" />{t("vs.honestly")}</h2>
            <div className="space-y-3">
              {vs.disadvantages.map((d, i) => (
                <div key={i} className="flex items-start gap-3 bg-slate-50 rounded-xl p-4">
                  <X className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-600">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-[#1A3F6E]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white">{t("vs.switch")}</h2>
          <p className="mt-4 text-lg text-slate-300">
            {maxSaving > 0 ? `${t("vs.save_up")} ${maxSaving}% ${t("vs.cheaper")} ${vs.name}.` : `${t("vs.more_features")} ${vs.name}.`}
          </p>
          <Link to="/register">
            <Button size="lg" className="mt-8 bg-[#2CA8AC] hover:bg-[#3BB5B9] text-lg px-10 h-14 rounded-full shadow-xl shadow-[#2CA8AC]/30">
              {t("vs.start_free")} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-slate-200">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <img src="/logo.jpg" alt="Smartpriced" className="h-8" />
          <div className="flex items-center gap-4 text-xs text-slate-400">
            <Link to="/terms" className="hover:text-[#2CA8AC]">Voorwaarden</Link>
            <Link to="/privacy" className="hover:text-[#2CA8AC]">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
